import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [platformDropdownOpen, setPlatformDropdownOpen] = useState(false);
    const [documintDropdownOpen, setDocumintDropdownOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navItems = [
        { name: 'How it Works', path: '/how-it-works' },
        { name: 'Use Cases', path: '/case-studies' },
        { name: 'Pricing', path: '/pricing' },
    ];

    const platformDropdownItems = [
        { 
            name: 'Platform Overview', 
            path: '/platform-overview',
            description: 'Comprehensive overview of Documint\'s identity platform capabilities.'
        },
        { 
            name: 'Tech Behind Documint Platform', 
            path: '/tech-platform',
            description: 'Deep dive into the technology powering Documint\'s identity infrastructure.'
        },
        { 
            name: 'Issue Verifiable Credentials', 
            path: '/issue-credentials',
            description: 'Create verifiable credentials through Documint\'s web app or API.'
        },
        { 
            name: 'Standards-based ID wallet', 
            path: '/id-wallet',
            description: 'Launch your own ID wallet using Documint\'s white-Label wallet or Wallet SDK.'
        },
        { 
            name: 'Digital ID ecosystem', 
            path: '/digital-ecosystem',
            description: 'Create a network of trusted digital identity issuers and verifiers.'
        },
        { 
            name: 'Privacy-Preserving Credential Monetization', 
            path: '/credential-monetization',
            description: 'Set a price for the verification of your Reusable ID credentials.'
        },
        { 
            name: 'mDL Verification', 
            path: '/mdl-verification',
            description: 'Easily embed mDL (Mobile Driver\'s License) verification into your existing workflow.'
        },
        { 
            name: 'India Digital Public Infrastructure', 
            path: '/india-dpi',
            description: 'Seamless integration with Aadhaar, DigiLocker, UPI & India Stack for Digital India mission.'
        }
    ];

    const documintDropdownItems = [
        { 
            name: 'Why Documint', 
            path: '/documint-id',
            section: 'why-documint',
            description: 'As our identity becomes more digital, the demand for Documint technology is growing. Here\'s why.'
        },
        { 
            name: 'How Documint works', 
            path: '/documint-id',
            section: 'how-documint-works',
            description: 'Documint allows companies to generate tamper-proof digital credentials containing user information or identifiers.'
        },
        { 
            name: 'Demos', 
            path: '/documint-id',
            section: 'demos',
            description: 'See how Documint accelerates customer onboarding and enables the creation of a new ecosystem of verifiers.'
        }
    ];

    return (
        <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-surface-100/80 backdrop-blur-md shadow-glow' : 'bg-transparent'}`}>
            <div className="container-custom">
                <div className="flex items-center justify-between h-16 lg:h-20">
                    {/* Logo */}
                    <Link to="/" className="flex items-center">
                        <img 
                            src="/assets/images/logo.png"
                            alt="Documint Logo" 
                            className="h-10 w-auto object-contain"
                        />
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-8">
                        {/* Platform Dropdown */}
                        <div className="relative">
                            <button
                                onMouseEnter={() => setPlatformDropdownOpen(true)}
                                onMouseLeave={() => setPlatformDropdownOpen(false)}
                                className="flex items-center space-x-1 text-sm font-medium text-gray-300 hover:text-white transition-colors duration-200"
                            >
                                <span>Platform</span>
                                <ChevronDown size={16} className={`transition-transform duration-200 ${platformDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            
                            <AnimatePresence>
                                {platformDropdownOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        transition={{ duration: 0.2 }}
                                        onMouseEnter={() => setPlatformDropdownOpen(true)}
                                        onMouseLeave={() => setPlatformDropdownOpen(false)}
                                        className="absolute top-full left-0 mt-2 w-[800px] bg-surface-100/95 backdrop-blur-md border border-white/10 rounded-lg shadow-xl z-50"
                                    >
                                        <div className="p-6">
                                            <div className="grid grid-cols-2 gap-4">
                                                {platformDropdownItems.map((item) => (
                                                    <Link
                                                        key={item.name}
                                                        to={item.path}
                                                        className="block p-4 rounded-lg hover:bg-white/5 transition-colors duration-200"
                                                    >
                                                        <div className="font-medium text-white mb-2">
                                                            {item.name}
                                                        </div>
                                                        <div className="text-sm text-gray-400 leading-relaxed">
                                                            {item.description}
                                                        </div>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Documint Dropdown */}
                        <div className="relative">
                            <button
                                onMouseEnter={() => setDocumintDropdownOpen(true)}
                                onMouseLeave={() => setDocumintDropdownOpen(false)}
                                className="flex items-center space-x-1 text-sm font-medium text-gray-300 hover:text-white transition-colors duration-200"
                            >
                                <span>Documint</span>
                                <ChevronDown size={16} className={`transition-transform duration-200 ${documintDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            
                            <AnimatePresence>
                                {documintDropdownOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        transition={{ duration: 0.2 }}
                                        onMouseEnter={() => setDocumintDropdownOpen(true)}
                                        onMouseLeave={() => setDocumintDropdownOpen(false)}
                                        className="absolute top-full left-0 mt-2 w-80 bg-surface-100/95 backdrop-blur-md border border-white/10 rounded-lg shadow-xl z-50"
                                    >
                                        <div className="p-4 space-y-3">
                                            {documintDropdownItems.map((item) => (
                                                <Link
                                                    key={item.name}
                                                    to={item.path}
                                                    onClick={() => {
                                                        if (item.section) {
                                                            setTimeout(() => {
                                                                const element = document.getElementById(item.section);
                                                                if (element) {
                                                                    element.scrollIntoView({ behavior: 'smooth' });
                                                                }
                                                            }, 100);
                                                        }
                                                    }}
                                                    className="block p-3 rounded-lg hover:bg-white/5 transition-colors duration-200"
                                                >
                                                    <div className="font-medium text-white mb-1">
                                                        {item.name}
                                                    </div>
                                                    <div className="text-sm text-gray-400 leading-relaxed">
                                                        {item.description}
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Documint Wallet Link */}
                        <Link
                            to="/documint-wallet"
                            className="text-sm font-medium text-gray-300 hover:text-white transition-colors duration-200"
                        >
                            Documint Wallet
                        </Link>

                        {navItems.map((item) => (
                            <Link
                                key={item.name}
                                to={item.path}
                                className={`text-sm font-medium transition-colors duration-200 ${location.pathname === item.path
                                    ? 'text-neon-300'
                                    : 'text-gray-300 hover:text-white'
                                    }`}
                            >
                                {item.name}
                            </Link>
                        ))}
                    </div>

                    {/* CTA Buttons */}
                    <div className="hidden md:flex items-center space-x-4">
                        <a
                            href="https://app.passhub.documint.id/login"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-secondary"
                        >
                            Login
                        </a>
                        <a
                            href="https://app.passhub.documint.id/signup"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary"
                        >
                            Sign Up
                        </a>
                    </div>

                    {/* Mobile menu button */}
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="md:hidden p-2 rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                        {isOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>

                {/* Mobile Navigation */}
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="md:hidden bg-surface-100/90 backdrop-blur border-t border-white/10"
                        >
                            <div className="px-4 py-6 space-y-4">
                                {/* Platform Mobile Dropdown */}
                                <div className="space-y-2">
                                    <div className="font-medium text-white text-base mb-2">Platform</div>
                                    {platformDropdownItems.map((item) => (
                                        <Link
                                            key={item.name}
                                            to={item.path}
                                            onClick={() => setIsOpen(false)}
                                            className="block pl-4 py-2 text-sm text-gray-300 hover:text-white"
                                        >
                                            {item.name}
                                        </Link>
                                    ))}
                                </div>

                                {/* Documint Mobile Dropdown */}
                                <div className="space-y-2">
                                    <div className="font-medium text-white text-base mb-2">Documint</div>
                                    {documintDropdownItems.map((item) => (
                                        <Link
                                            key={item.name}
                                            to={item.path}
                                            onClick={() => {
                                                setIsOpen(false);
                                                if (item.section) {
                                                    setTimeout(() => {
                                                        const element = document.getElementById(item.section);
                                                        if (element) {
                                                            element.scrollIntoView({ behavior: 'smooth' });
                                                        }
                                                    }, 100);
                                                }
                                            }}
                                            className="block pl-4 py-2 text-sm text-gray-300 hover:text-white"
                                        >
                                            {item.name}
                                        </Link>
                                    ))}
                                </div>

                                {/* Documint Wallet Mobile Link */}
                                <Link
                                    to="/documint-wallet"
                                    onClick={() => setIsOpen(false)}
                                    className="block text-base font-medium text-gray-300 hover:text-white"
                                >
                                    Documint Wallet
                                </Link>

                                {navItems.map((item) => (
                                    <Link
                                        key={item.name}
                                        to={item.path}
                                        onClick={() => setIsOpen(false)}
                                        className={`block text-base font-medium ${location.pathname === item.path
                                            ? 'text-neon-300'
                                            : 'text-gray-300 hover:text-white'
                                            }`}
                                    >
                                        {item.name}
                                    </Link>
                                ))}
                                <div className="pt-4 space-y-3">
                                    <a
                                        href="https://app.passhub.documint.id/login"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => setIsOpen(false)}
                                        className="block w-full text-center btn-secondary"
                                    >
                                        Login
                                    </a>
                                    <a
                                        href="https://app.passhub.documint.id/signup"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => setIsOpen(false)}
                                        className="block w-full text-center btn-primary"
                                    >
                                        Sign Up
                                    </a>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </nav>
    );
};

export default Navbar;
